import { useEffect, useRef, useState } from 'react'
import { AuthAPI } from '../../services/auth'
import { setToken } from '../../utils/authStorage'
import { showToast } from '../../utils/showToast'
import { isValidEmail, isValidPassword } from './validation'
import PasswordField from './PasswordField'
import './AuthDialog.css'

interface SignupDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function SignupDialog({ open, onClose, onSuccess }: SignupDialogProps) {
  const [step, setStep] = useState<'first' | 'second'>('first')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [passwordError, setPasswordError] = useState(false)
  const [codeError, setCodeError] = useState(false)
  const [resendSeconds, setResendSeconds] = useState(0)
  const timerRef = useRef<number | null>(null)

  const reset = () => {
    setStep('first')
    setEmail('')
    setPassword('')
    setCode('')
    setEmailError(false)
    setPasswordError(false)
    setCodeError(false)
    setResendSeconds(0)
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  useEffect(() => {
    if (!open) reset()
  }, [open])

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current)
    }
  }, [])

  const startResendTimer = () => {
    setResendSeconds(60)
    if (timerRef.current) window.clearInterval(timerRef.current)
    timerRef.current = window.setInterval(() => {
      setResendSeconds((s) => {
        if (s <= 1) {
          if (timerRef.current) window.clearInterval(timerRef.current)
          return 0
        }
        return s - 1
      })
    }, 1000)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleNext = async () => {
    const validEmail = isValidEmail(email)
    const validPassword = isValidPassword(password)
    setEmailError(!validEmail)
    setPasswordError(!validPassword)
    if (!validEmail || !validPassword) return

    try {
      await AuthAPI.sendEmailCode(email)
      startResendTimer()
      setStep('second')
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Network error')
    }
  }

  const handleResend = async () => {
    if (resendSeconds > 0) return
    try {
      await AuthAPI.sendEmailCode(email)
      startResendTimer()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Network error')
    }
  }

  const handleSignup = async () => {
    const validCode = code.trim().length > 0
    setCodeError(!validCode)
    if (!validCode) return

    try {
      const result = await AuthAPI.emailRegister(email, password, code.trim())
      setToken(result.token)
      showToast('Registration success')
      handleClose()
      setTimeout(() => onSuccess(), 1000)
    } catch (e) {
      setCodeError(true)
      showToast(e instanceof Error ? e.message : 'Verification code error')
    }
  }

  if (!open) return null

  return (
    <div className="c-dialog-wrapper" role="dialog" aria-modal="true" aria-label="Sign up">
      <div className="c-dialog dialog-signup">
        <div className="c-dialog-header">
          <button type="button" className="close" aria-label="Close" onClick={handleClose} />
        </div>

        {step === 'first' && (
          <div className="c-dialog-content step-first">
            <p className="title">Sign up</p>
            <div className="form">
              <div className="form-item">
                <span className="label">Email address</span>
                <input
                  className="input-text"
                  type="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {emailError && <p className="help">Format error, please re-enter</p>}
              </div>
              <PasswordField
                value={password}
                onChange={setPassword}
                showError={passwordError}
              />
            </div>
            <button type="button" className="btn" onClick={handleNext}>Next</button>
          </div>
        )}

        {step === 'second' && (
          <div className="c-dialog-content step-second">
            <p className="title">Verify email</p>
            <div className="form">
              <div className="form-item">
                <span className="label">Enter verification code</span>
                <input
                  className="input-text"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                {codeError && <p className="help">Verification code error, please re-enter</p>}
                <p className="help-resent">
                  Did not get verification code?{' '}
                  <button
                    type="button"
                    className="link-btn"
                    disabled={resendSeconds > 0}
                    onClick={handleResend}
                  >
                    {resendSeconds > 0 ? `Resent(${resendSeconds})` : 'Resent'}
                  </button>
                </p>
              </div>
            </div>
            <button type="button" className="btn" onClick={handleSignup}>Sign up</button>
          </div>
        )}
      </div>
    </div>
  )
}
